import {
  Link as RouterLink,
  Navigate,
  Outlet,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  capitalize,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import { Space } from "~/lib/types";
import { Home as HomeIcon } from "@mui/icons-material";
import { TabNavigation, useRouteMatch } from "~/components/tab-nav";
import { useSpaceSettingsPermissions } from "~/hooks/useSpaceSettingsPermissions";
import { setDocumentTitle } from "~/lib/commonUtils";
import { TargetType } from "~/components/FormFields/types";

interface SpaceSettingsProps {
  space: Space;
}

export function SpaceSettings({ space }: SpaceSettingsProps) {
  const [searchParams] = useSearchParams();
  const spaceId = searchParams.get("spaceId");
  const workspaceMrn = searchParams.get("workspaceId");
  const isWorkspace = !!workspaceMrn;
  const targetType: TargetType = isWorkspace ? "workspace" : "space";
  const scopeParams = new URLSearchParams();
  if (spaceId) scopeParams.append("spaceId", spaceId);
  if (workspaceMrn) scopeParams.append("workspaceId", workspaceMrn);

  const href = (tab: string) => {
    return `/space/settings/${tab}?${scopeParams}`;
  };

  const {
    canGetSpace,
    canListMembers,
    canListIntegrations,
    canViewRegistrationTokens,
    canListApiTokens,
    canListServiceAccounts,
    canListWifAuthBindings,
    canListLogs,
  } = useSpaceSettingsPermissions(space);

  const generalTab = {
    label: "General Settings",
    to: href("general"),
    route: "/general",
    permitted: canGetSpace,
  };

  const otherTabs = [
    {
      label: "Ticketing",
      to: href("tickets"),
      route: "/tickets",
      permitted: canListIntegrations,
    },
    {
      label: "Members",
      to: href("members"),
      route: "/members",
      permitted: canListMembers,
    },
    {
      label: "Audit Log",
      to: href("auditlog"),
      route: "/auditlog",
      permitted: canListLogs,
    },
    {
      label: "Service Accounts",
      to: href("serviceaccounts"),
      route: "/serviceaccounts",
      permitted: canListServiceAccounts,
    },
    {
      label: "Registration Tokens",
      to: href("registration-tokens"),
      route: "/registration-tokens",
      permitted: canViewRegistrationTokens,
    },
    {
      label: "Workload Identity",
      to: href("workload-identity"),
      route: "/workload-identity",
      permitted: canListWifAuthBindings,
    },
    {
      label: "API Tokens",
      to: href("api-tokens"),
      route: "/api-tokens",
      permitted: canListApiTokens,
    },
  ].sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

  const allTabs = [generalTab, ...otherTabs];
  const tabList = allTabs.filter((tab) => tab.permitted);

  const currentTab = useRouteMatch(
    allTabs.map((x) => x.route),
    "general",
  );
  const currentTabLabel =
    tabList.find((tab) => tab.route.endsWith(currentTab))?.label || "";

  // Tabs are hidden when the user lacks permission, but their routes are
  // still reachable by direct navigation — redirect to the first permitted
  // tab (or the space overview if there is none). A route that matches no
  // tab is treated as permitted so future sub-routes are not redirected.
  const currentTabPermitted =
    allTabs.find((tab) => tab.route === currentTab)?.permitted ?? true;
  if (!isWorkspace && !currentTabPermitted) {
    return (
      <Navigate
        to={tabList[0]?.to ?? `/space/overview?spaceId=${space.id}`}
        replace
      />
    );
  }

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?spaceId=${space.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography key={1}>Settings</Typography>,
  ];

  setDocumentTitle([currentTabLabel, "Settings", space.name]);

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
          {breadcrumbs}
        </Breadcrumbs>
        {/* Heading */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {capitalize(targetType)} Settings
          </Typography>
        </Box>
        {!isWorkspace && (
          <TabNavigation
            {...{
              id: "space-settings-tabs",
              tabList,
              currentTab,
              marginBottom: 1,
            }}
          />
        )}
      </Grid>
      <Grid size="grow">
        <Outlet />
      </Grid>
    </Grid>
  );
}
